import { useEffect, useState, useMemo, useRef } from "react";
import { axInst } from "../../../Components/Login/Service";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "rsuite";
import DualGauge from "../../../Components/Charts/SpeedGauge";
import WaterMark from "../../../Components/WaterMark/WaterMark.jsx";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";

/* ================= API CALLS ================= */

const WarScore_api = async (body) => {
  try {
    const res = await axInst.post("dealer-review/war_score", body);
    return res.data;
  } catch (error) {
    console.error("Error fetching warscore data:", error);
    throw error;
  }
};

const fetchDivisionDropdown = async () => {
  try {
    const res = await axInst.post("dealer-review/divison_filter");
    return res.data?.distinct_divisions?.buckets ?? [];
  } catch (error) {
    console.error("Dropdown API Error:", error);
    return [];
  }
};

/* ================= COMPONENT ================= */

const WarScore = ({ show }) => {
  const [selectedOption, setSelectedOption] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tempSelectedOption, setTempSelectedOption] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const { data, submitted } = useSelector((e) => e.filtersDealerReview);

  /* ---------- Fetch Division Dropdown ---------- */
  useEffect(() => {
    const fetchData = async () => {
      const divisions = await fetchDivisionDropdown();
      setDivisionOptions(divisions);
    };
    fetchData();
  }, []);

  /* ---------- Close dropdown on outside click ---------- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------- Query Body ---------- */
  const queryBody = useMemo(
    () => ({
      ...data,
      division_name: selectedOption.length ? selectedOption : [],
    }),
    [data, selectedOption],
  );

  /* ---------- Dropdown Handlers ---------- */
  const handleCheckboxChange = (e) => {
    const value = e.target.value;
    setTempSelectedOption((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const clearAll = () => setTempSelectedOption([]);

  const handleApplyFilters = () => {
    setSelectedOption(tempSelectedOption);
    setDropdownOpen(false);
  };

  useEffect(() => {
    if (dropdownOpen) {
      setTempSelectedOption(selectedOption);
    }
  }, [dropdownOpen, selectedOption]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const filteredDivisionOptions = divisionOptions.filter((option) =>
    option.key.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  /* ---------- WAR SCORE QUERY ---------- */
  const {
    data: war_Data,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["warScore", submitted, selectedOption],
    queryFn: () => WarScore_api(queryBody),
    cacheTime: 0,
    staleTime: 0,
  });

  /* ---------- NORMALIZE DATA (H1 & H2 WITH OWN FY) ---------- */
  const normalizedWarData = useMemo(() => {
    if (!Array.isArray(war_Data)) return null;

    let result = {
      H1: 0,
      H1_FY: null,
      H2: 0,
      H2_FY: null,
    };

    war_Data.forEach((item) => {
      if (item?.scores?.H1 !== undefined) {
        result.H1 = item.scores.H1;
        result.H1_FY = item.fiscal_year;
      }
      if (item?.scores?.H2 !== undefined) {
        result.H2 = item.scores.H2;
        result.H2_FY = item.fiscal_year;
      }
    });

    return result;
  }, [war_Data]);

  /* ---------- STATES ---------- */
  if (isFetching) {
    return (
      <div className="grid items-center justify-center w-full h-full">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="grid items-center justify-center w-full h-full">
        Error loading data.
      </div>
    );
  }

  if (!normalizedWarData || (!normalizedWarData.H1 && !normalizedWarData.H2)) {
    return (
      <div className="grid items-center justify-center w-full h-full text-gray-500">
        Data not available
      </div>
    );
  }

  /* ---------- RENDER ---------- */
  return (
    <div className="bg-white rounded-md shadow-lg p-2 relative h-full min-h-[220px] sm:min-h-[300px]">
      <WaterMark />

      <div className="flex justify-between items-center">
        <div className="text-lg font-bold text-gray-800 pl-2">War Score</div>

        {!show && (
          <div className="my-1 flex justify-end">
            <div className="relative">
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="border border-gray-300 text-[12px] bg-white rounded-md w-48 p-0.5 flex items-center justify-between px-2"
              >
                {selectedOption.length > 0
                  ? `Selected Divisions (${selectedOption.length})`
                  : "Select Divisions"}
                <span
                  className={`ml-2 transform transition-transform text-xl ${
                    dropdownOpen ? "rotate-180" : "rotate-0"
                  }`}
                >
                  <MdOutlineKeyboardArrowDown />
                </span>
              </button>

              {dropdownOpen && (
                <div
                  ref={dropdownRef}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                >
                  <div className="p-2">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search Division..."
                      className="w-full p-1 border border-gray-300 rounded mb-1"
                    />

                    <div className="flex justify-end px-2">
                      <button
                        className="text-blue-600 hover:text-blue-800 text-xs"
                        onClick={clearAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="max-h-48 overflow-auto">
                      {filteredDivisionOptions.map((option, index) => (
                        <label key={index} className="block text-sm">
                          <input
                            type="checkbox"
                            value={option.key}
                            checked={tempSelectedOption.includes(option.key)}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                          />
                          {option.key}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="sticky bottom-0 bg-white border-t border-gray-200">
                    <button
                      className="px-3 py-1 bg-[#2A5775] w-full text-white text-xs"
                      onClick={handleApplyFilters}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <DualGauge
        key={`${normalizedWarData.H1}-${normalizedWarData.H2}`}
        H1={normalizedWarData.H1}
        H2={normalizedWarData.H2}
        H1_FY={normalizedWarData.H1_FY}
        H2_FY={normalizedWarData.H2_FY}
      />
    </div>
  );
};

export default WarScore;
